import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Form, Checkbox, Message } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import styled from 'styled-components'
import { X } from 'phosphor-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { useAuth } from 'context'
import { useNotification } from 'hooks/toast'
import { UsersService } from 'utility/services/user'
import { isPlanLimitErrorCode, getPlanLimitCode } from 'utility/plan-limit-events'
import { TemplateService } from 'utility/services/template'
import { CustomerService } from 'utility/services/customer'
import { CustomerUserService } from 'utility/services/customer-user'
import { GroupService } from 'utility/services/group'
import { isRoleModelOn } from 'utility/role-model'

const SUPER_ADMIN_ROLE_ID = 1000

const ModalHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

const HelpText = styled.p`
    margin: 0 0 6px 0;
    font-size: 11.5px;
    color: #6b7280;
`

const FieldError = styled.div`
    color: #dc2626;
    font-size: 12px;
    margin-top: 4px;
`

const PasswordRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: stretch;
    .ui.input {
        flex: 1;
    }
`

const ToggleRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 12px;
    background: #f9fafb;
    border-radius: 5px;
    border: 1px solid #e5e7eb;
    .txt strong {
        display: block;
        font-size: 12.5px;
        margin-bottom: 2px;
    }
    .txt small {
        color: #6b7280;
        font-size: 11px;
        line-height: 1.4;
    }
`

const SecurityNote = styled.div`
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.5;
    background: #fef3c7;
    border: 1px solid #fde68a;
    color: #713f12;
    margin-top: 4px;
`

const ModalFooter = styled(Modal.Actions)`
    display: flex !important;
    justify-content: flex-end;
    gap: 8px;
    background: #f9fafb !important;
`

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%'
    return Array.from(crypto.getRandomValues(new Uint8Array(12)))
        .map(x => chars[x % chars.length])
        .join('')
}

const emptyForm = () => ({
    FullName: '',
    Email: '',
    Password: '',
    RoleId: null,
    TemplateIds: [],
    CustomerIds: [],
    SendWelcomeEmail: true,
    ShowPhotoGps: true,
})

const validateClient = form => {
    const errors = {}
    const name = (form.FullName || '').trim()
    if (!name) {
        errors.FullName = 'Full name is required.'
    } else if (name.length > 100) {
        errors.FullName = 'Full name must be at most 100 characters.'
    }

    const email = (form.Email || '').trim()
    if (!email) {
        errors.Email = 'Email is required.'
    } else if (!EMAIL_RE.test(email)) {
        errors.Email = 'Enter a valid email address.'
    }

    const password = form.Password || ''
    if (!password) {
        errors.Password = 'Password is required.'
    } else if (password.length < 8) {
        errors.Password = 'Password must be at least 8 characters.'
    } else if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
        errors.Password = 'Password must contain at least 1 letter and 1 digit.'
    }

    if (!form.RoleId || form.RoleId <= 0) {
        errors.RoleId = 'Role is required.'
    }

    return errors
}

const normalizeRoleOptions = (roles, isSuperAdmin) => {
    const list = Array.isArray(roles) ? roles : []
    return list
        .map(role => {
            const id = role.RollId ?? role.RoleId ?? role.roleId
            const label =
                role.RollDescription ??
                role.RoleName ??
                role.RoleDescription ??
                String(id)
            return {
                key: id,
                value: id,
                text: label,
            }
        })
        .filter(opt => opt.value != null)
        .filter(opt => isSuperAdmin || opt.value !== SUPER_ADMIN_ROLE_ID)
}

const normalizeTemplateOptions = templates => {
    const items = Array.isArray(templates)
        ? templates
        : templates?.Items || []
    return items
        .map(t => {
            // MyTemplates: Id = numeric int, TemplateId = GUID.
            // AdminCreate expects int[] — always use numeric Id.
            const id = Number(t.Id ?? t.id)
            if (!id || Number.isNaN(id)) return null
            const name = t.TemplateName ?? t.Name ?? String(id)
            return { key: id, value: id, text: name }
        })
        .filter(Boolean)
}

const normalizeCustomerOptions = customers => {
    const items = Array.isArray(customers)
        ? customers
        : customers?.Items || []
    const seen = new Set()
    const options = []
    items.forEach(c => {
        let id = c.CustomerId ?? c.unique_id ?? c.Id
        if (typeof id === 'string' && id.includes('-')) {
            const parts = id.split('-')
            if (parts.length === 2 && /^\d+$/.test(parts[0])) {
                id = Number(parts[0])
            }
        }
        if (id == null || seen.has(id)) return
        seen.add(id)
        const name =
            c.CustomerName ?? c.customer_name ?? c.DisplayText ?? String(id)
        options.push({ key: id, value: id, text: name })
    })
    return options
}

export const CreateUserModal = ({ open, onClose, onCreated, companyId }) => {
    const { user } = useAuth()
    const { successToast, errorToast, warningToast } = useNotification()
    const isSuperAdmin = user?.IsSuperAdmin === true

    const [form, setForm] = useState(emptyForm)
    const [fieldErrors, setFieldErrors] = useState({})
    const [modalError, setModalError] = useState('')
    const [loadingLookups, setLoadingLookups] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const [roleOptions, setRoleOptions] = useState([])
    const [templateOptions, setTemplateOptions] = useState([])
    const [customerOptions, setCustomerOptions] = useState([])

    const fullNameRef = useRef(null)
    const emailRef = useRef(null)
    const passwordRef = useRef(null)

    const resolvedCompanyId =
        companyId ||
        user?.CompanyId ||
        user?.Companies?.[0]?.CompanyId

    useEffect(() => {
        if (!open) return undefined

        setForm(emptyForm())
        setFieldErrors({})
        setModalError('')
        setSubmitting(false)

        let cancelled = false
        const loadLookups = async () => {
            setLoadingLookups(true)
            try {
                const [rolesRes, templatesRes, customersRes] =
                    await Promise.all([
                        // own company on the department role model: its five roles (Admin = 1000 stays hidden
                        // here - Users/AdminCreate keeps 1000 for super admins; invite an Admin instead)
                        !isSuperAdmin && isRoleModelOn(user)
                            ? GroupService.getUserRoleTypes()
                            : UsersService.getRoles(),
                        TemplateService.templateList({
                            PageNumber: 1,
                            PageSize: 5000000,
                            SearchText: '',
                            all: 'All',
                        }),
                        resolvedCompanyId
                            ? CustomerUserService.getByCompany(
                                  resolvedCompanyId,
                              ).catch(() =>
                                  CustomerService.list({
                                      PageNumber: 1,
                                      PageSize: 5000000,
                                      SearchText: '',
                                      all: 'All',
                                  }),
                              )
                            : CustomerService.list({
                                  PageNumber: 1,
                                  PageSize: 5000000,
                                  SearchText: '',
                                  all: 'All',
                              }),
                    ])

                if (cancelled) return
                setRoleOptions(normalizeRoleOptions(rolesRes, isSuperAdmin))
                setTemplateOptions(normalizeTemplateOptions(templatesRes))
                setCustomerOptions(normalizeCustomerOptions(customersRes))
            } catch (err) {
                console.error('Error loading create-user lookups:', err)
                if (!cancelled) {
                    setModalError(
                        'Failed to load roles, templates, or customers. Please close and try again.',
                    )
                }
            } finally {
                if (!cancelled) setLoadingLookups(false)
            }
        }

        loadLookups()
        return () => {
            cancelled = true
        }
    }, [open, resolvedCompanyId, isSuperAdmin])

    const setField = (name, value) => {
        setForm(prev => ({ ...prev, [name]: value }))
        setFieldErrors(prev => {
            if (!prev[name]) return prev
            const next = { ...prev }
            delete next[name]
            return next
        })
        setModalError('')
    }

    const requiredFilled = useMemo(() => {
        const name = (form.FullName || '').trim()
        const email = (form.Email || '').trim()
        return (
            !!name &&
            !!email &&
            !!form.Password &&
            !!form.RoleId &&
            !loadingLookups
        )
    }, [form, loadingLookups])

    const focusField = field => {
        const map = {
            FullName: fullNameRef,
            Email: emailRef,
            Password: passwordRef,
        }
        const ref = map[field]
        if (ref?.current) {
            ref.current.focus()
        }
    }

    const handleGeneratePassword = () => {
        const pwd = generatePassword()
        setField('Password', pwd)
    }

    const handleSubmit = async () => {
        const clientErrors = validateClient(form)
        if (Object.keys(clientErrors).length) {
            setFieldErrors(clientErrors)
            const first = Object.keys(clientErrors)[0]
            focusField(first)
            return
        }

        setSubmitting(true)
        setModalError('')
        setFieldErrors({})

        try {
            const toIntIds = ids =>
                (ids || [])
                    .map(id => Number(id))
                    .filter(id => !Number.isNaN(id) && id > 0)

            const payload = {
                FullName: form.FullName.trim(),
                Email: form.Email.trim(),
                Password: form.Password,
                RoleId: Number(form.RoleId),
                ...(resolvedCompanyId
                    ? { CompanyId: Number(resolvedCompanyId) }
                    : {}),
                TemplateIds: toIntIds(form.TemplateIds),
                CustomerIds: toIntIds(form.CustomerIds),
                SendWelcomeEmail: !!form.SendWelcomeEmail,
                ShowPhotoGps: !!form.ShowPhotoGps,
            }

            const result = await UsersService.adminCreate(payload)

            if (result?.Success === false) {
                const code = result.ErrorCode
                const field = result.Field
                const message =
                    result.ErrorMessage || 'Could not create user.'

                if (
                    (code === 'InvalidValue' || code === 'DuplicateEmail') &&
                    field
                ) {
                    setFieldErrors({ [field]: message })
                    focusField(field)
                } else if (code === 'NotAuthorized') {
                    setModalError(message)
                } else {
                    setModalError(message)
                }
                return
            }

            onClose()

            if (result?.WelcomeEmailSent === false && form.SendWelcomeEmail) {
                warningToast(
                    `User created, but welcome email failed. ${
                        result.WelcomeEmailError ||
                        'Communicate the credentials to the user manually.'
                    }`,
                )
            } else {
                const templatesAssigned = result?.TemplatesAssigned ?? 0
                const customersAssigned = result?.CustomersAssigned ?? 0
                successToast(
                    `User created. Welcome email ${
                        result?.WelcomeEmailSent
                            ? `sent to ${result.Email || form.Email}`
                            : 'not sent'
                    }. ${templatesAssigned} templates and ${customersAssigned} customers assigned.`,
                )
            }

            if (typeof onCreated === 'function') {
                onCreated(result)
            }
        } catch (err) {
            const status = err?.status
            const data = err?.error || {}
            const code = getPlanLimitCode(data) || data.ErrorCode
            const field = data.Field
            const message = data.ErrorMessage || data.message

            // Global PlanLimitBlockDialog handles PLAN_* 403s.
            if (status === 403 && isPlanLimitErrorCode(code)) {
                return
            }

            if (status === 400 && (code === 'InvalidValue' || code === 'DuplicateEmail') && field) {
                setFieldErrors({ [field]: message || 'Invalid value.' })
                focusField(field)
            } else if (status === 403 || code === 'NotAuthorized') {
                setModalError(
                    message ||
                        'Only a Super Admin can create a Super Admin user.',
                )
            } else if (!status) {
                errorToast('Cannot reach server — check connection.')
            } else if (status >= 500) {
                setModalError(
                    "Couldn't create user — server error, please retry.",
                )
                errorToast(
                    "Couldn't create user — server error, please retry.",
                )
            } else {
                setModalError(message || "Couldn't create user. Please retry.")
            }
        } finally {
            setSubmitting(false)
        }
    }

    const disabled = submitting || loadingLookups

    return (
        <Modal
            open={open}
            onClose={() => !submitting && onClose()}
            size="small"
            closeOnDimmerClick={!submitting}
            closeOnEscape={!submitting}
        >
            <Modal.Header>
                <ModalHeaderRow>
                    <span>Add New User</span>
                    <X
                        className="pointer"
                        size={22}
                        onClick={() => !submitting && onClose()}
                        style={{ color: '#6b7280' }}
                    />
                </ModalHeaderRow>
            </Modal.Header>
            <Modal.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {modalError && (
                    <Message negative onDismiss={() => setModalError('')}>
                        {modalError}
                    </Message>
                )}
                <Form noValidate>
                    <Form.Field error={!!fieldErrors.FullName} required>
                        <label>Full Name</label>
                        <HelpText>Displayed everywhere in the app.</HelpText>
                        <input
                            ref={fullNameRef}
                            type="text"
                            placeholder="Sarah Chen"
                            value={form.FullName}
                            disabled={disabled}
                            maxLength={100}
                            onChange={e => setField('FullName', e.target.value)}
                        />
                        {fieldErrors.FullName && (
                            <FieldError>{fieldErrors.FullName}</FieldError>
                        )}
                    </Form.Field>

                    <Form.Group widths="equal">
                        <Form.Field error={!!fieldErrors.Email} required>
                            <label>Email</label>
                            <HelpText>
                                Also becomes the login and the welcome-email
                                destination.
                            </HelpText>
                            <input
                                ref={emailRef}
                                type="email"
                                placeholder="sarah@cintas.com"
                                value={form.Email}
                                disabled={disabled}
                                onChange={e =>
                                    setField('Email', e.target.value)
                                }
                            />
                            {fieldErrors.Email && (
                                <FieldError>{fieldErrors.Email}</FieldError>
                            )}
                        </Form.Field>

                        <Form.Field error={!!fieldErrors.Password} required>
                            <label>Password</label>
                            <HelpText>
                                Min 8 chars, 1 letter + 1 digit.
                            </HelpText>
                            <PasswordRow>
                                <div className="ui input">
                                    <input
                                        ref={passwordRef}
                                        type="text"
                                        placeholder="•••••••••"
                                        value={form.Password}
                                        disabled={disabled}
                                        onChange={e =>
                                            setField('Password', e.target.value)
                                        }
                                        autoComplete="new-password"
                                    />
                                </div>
                                <AppSecondaryButton
                                    type="button"
                                    disabled={disabled}
                                    onClick={handleGeneratePassword}
                                >
                                    Generate
                                </AppSecondaryButton>
                            </PasswordRow>
                            {fieldErrors.Password && (
                                <FieldError>{fieldErrors.Password}</FieldError>
                            )}
                        </Form.Field>
                    </Form.Group>

                    <Form.Field error={!!fieldErrors.RoleId} required>
                        <label>Role</label>
                        <HelpText>Loaded from GET /UserRole.</HelpText>
                        <Dropdown
                            placeholder="-- Select role --"
                            fluid
                            selection
                            search
                            selectOnBlur={false}
                            options={roleOptions}
                            value={form.RoleId}
                            loading={loadingLookups}
                            disabled={disabled}
                            onChange={(e, { value }) =>
                                setField('RoleId', value)
                            }
                        />
                        {fieldErrors.RoleId && (
                            <FieldError>{fieldErrors.RoleId}</FieldError>
                        )}
                    </Form.Field>

                    <Form.Field>
                        <label>
                            Assign Templates{' '}
                            <span style={{ color: '#6b7280', fontWeight: 400 }}>
                                — optional
                            </span>
                        </label>
                        <HelpText>Multi-select. Empty is fine.</HelpText>
                        <Dropdown
                            placeholder="Add template…"
                            fluid
                            multiple
                            search
                            selection
                            selectOnBlur={false}
                            options={templateOptions}
                            value={form.TemplateIds}
                            loading={loadingLookups}
                            disabled={disabled}
                            onChange={(e, { value }) =>
                                setField('TemplateIds', value)
                            }
                        />
                    </Form.Field>

                    <Form.Field>
                        <label>
                            Assign Customers{' '}
                            <span style={{ color: '#6b7280', fontWeight: 400 }}>
                                — optional
                            </span>
                        </label>
                        <HelpText>Multi-select. Empty is fine.</HelpText>
                        <Dropdown
                            placeholder="Add customer…"
                            fluid
                            multiple
                            search
                            selection
                            selectOnBlur={false}
                            options={customerOptions}
                            value={form.CustomerIds}
                            loading={loadingLookups}
                            disabled={disabled}
                            onChange={(e, { value }) =>
                                setField('CustomerIds', value)
                            }
                        />
                    </Form.Field>

                    <Form.Field>
                        <ToggleRow>
                            <Checkbox
                                toggle
                                checked={form.ShowPhotoGps}
                                disabled={disabled}
                                onChange={(e, { checked }) =>
                                    setField('ShowPhotoGps', !!checked)
                                }
                            />
                            <div className="txt">
                                <strong>Show GPS Caption in PDF</strong>
                                <small>
                                    When on (and company setting is on), GPS
                                    captions render under media photos in this
                                    user&apos;s audit PDFs.
                                </small>
                            </div>
                        </ToggleRow>
                    </Form.Field>

                    <Form.Field>
                        <ToggleRow>
                            <Checkbox
                                checked={form.SendWelcomeEmail}
                                disabled={disabled}
                                onChange={(e, { checked }) =>
                                    setField('SendWelcomeEmail', checked)
                                }
                            />
                            <div className="txt">
                                <strong>
                                    Send welcome email with credentials
                                </strong>
                                <small>
                                    Emails login URL + email + password to the
                                    new user immediately. Uncheck to communicate
                                    credentials manually.
                                </small>
                            </div>
                        </ToggleRow>
                    </Form.Field>

                    <SecurityNote>
                        <strong>Password security note:</strong> the plain-text
                        password is emailed once (server never stores it in
                        plaintext). If SES fails to send, the account still
                        exists and the response returns WelcomeEmailSent:false —
                        admin then communicates credentials manually.
                    </SecurityNote>
                </Form>
            </Modal.Content>
            <ModalFooter>
                <AppSecondaryButton
                    type="button"
                    disabled={submitting}
                    onClick={onClose}
                >
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton
                    type="button"
                    loading={submitting}
                    disabled={!requiredFilled || submitting}
                    onClick={handleSubmit}
                >
                    {form.SendWelcomeEmail
                        ? 'Create user & send email'
                        : 'Create user'}
                </AppPrimaryButton>
            </ModalFooter>
        </Modal>
    )
}
